import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/Card';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import api from '@/api';
import toast from 'react-hot-toast';
import { toArray } from "../utils/toArray";
import { splitDateCollab } from "../utils/splitDateCollab";
import { HiOutlineTrash, HiOutlinePencilSquare, HiOutlineArrowsRightLeft, HiOutlineDocumentArrowDown } from "react-icons/hi2";

const ITEMS_PER_PAGE = 10;

const OffresPage = () => {
    const [offres, setOffres] = useState([]);
    const [totalOffres, setTotalOffres] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchOffres = useCallback(async (page, search = '') => {
        setIsLoading(true);
        const skip = (page - 1) * ITEMS_PER_PAGE;
        try {
            const response = await api.get(`/offres/?skip=${skip}&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(search)}`);
            setOffres(Array.isArray(response.data.items) ? response.data.items : []);
            setTotalOffres(response.data.total || 0);
        } catch (error) {
            console.error("Erreur lors de la récupération des offres:", error);
            toast.error("Impossible de charger les offres.");
            setOffres([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOffres(currentPage, searchTerm);
    }, [fetchOffres, currentPage, searchTerm]);

    const handleDelete = async () => {
        const deleteToast = toast.loading('Suppression en cours...');
        try {
            await api.delete('/offres/', { data: { ids: selectedIds } });
            setOffres(prevOffres => 
                prevOffres.filter(offre => !selectedIds.includes(offre.id))
            );
            toast.success(`${selectedIds.length} offre(s) supprimée(s).`, { id: deleteToast });
            setSelectedIds([]);
        } catch (error) {
            console.error("Erreur lors de la suppression des offres:", error);
            toast.error('Une erreur est survenue.', { id: deleteToast });
        } finally {
            setIsModalOpen(false);
        }
    };

    const offresForTable = toArray(offres).map(offre => {
        const { date, collab } = splitDateCollab(offre.dateCollaboration || "");
        return {
            id: offre.id,
            date,
            collab,
            compte: offre.compte,
            secteur: offre.secteur,
            numeroReference: offre.numeroReference,
            numeroBH: offre.numeroBH,
            donneurDordre: offre.donneurDordre,
            detailDuBesoin: offre.detailDuBesoin,
            tjmAnnonce: offre.tjmAnnonce,
            niveauExp: offre.niveauExp,
            solution: offre.solution,
            statut: offre.statut,
            commentaire: offre.commentaire,
            attachment: (
                <div className="flex justify-center">
                    {offre.attachment_filename ? (
                        <a href={`/uploads/${offre.attachment_filename}`} target="_blank" rel="noopener noreferrer" className="text-pro-dark-text-secondary hover:text-pro-dark-accent" title={offre.attachment_filename}>
                            <HiOutlineDocumentArrowDown size={18} />
                        </a>
                    ) : ( <span className="text-gray-600">-</span> )}
                </div>
            ),
            matching: (
                <div className="flex justify-center">
                    <Link to={`/offres/${offre.id}/matching`} className="text-pro-dark-text-secondary hover:text-pro-dark-accent" title="Voir les candidats correspondants">
                        <HiOutlineArrowsRightLeft size={18} />
                    </Link>
                </div>
            ),
            actions: (
                <div className="flex justify-center">
                    <Link to={`/offres/modifier/${offre.id}`} className="text-pro-dark-text-secondary hover:text-pro-dark-accent" title="Modifier">
                        <HiOutlinePencilSquare size={18} />
                    </Link>
                </div>
            )
        };
    });

    const columns = [
        { Header: 'Matching', accessor: 'matching' },
        { Header: 'Actions', accessor: 'actions' },
        { Header: 'Fichier', accessor: 'attachment' },
        { Header: 'Date', accessor: 'date' },
        { Header: 'Collab', accessor: 'collab' },
        { Header: 'Compte', accessor: 'compte' },
        { Header: 'Secteur', accessor: 'secteur' },
        { Header: 'Numéro Référence', accessor: 'numeroReference' },
        { Header: 'Numéro BH', accessor: 'numeroBH' },
        { Header: 'Donneur D\'ordre', accessor: 'donneurDordre' },
        { Header: 'Détail du Besoin', accessor: 'detailDuBesoin' },
        { Header: 'TJM Annoncé', accessor: 'tjmAnnonce' },
        { Header: 'Niveau Exp', accessor: 'niveauExp' },
        { Header: 'Solution', accessor: 'solution' },
        { Header: 'Statut', accessor: 'statut' },
        { Header: 'Commentaire', accessor: 'commentaire' },
    ];

  return (
    <div>
        <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-pro-dark-text-primary">Appels d'offres</h1>
            <Link to="/offres/ajouter" className="px-4 py-2 bg-pro-dark-accent text-white rounded-lg hover:bg-pro-dark-accent-hover transition-colors">
            Ajouter une Offre
            </Link>
        </div>
        <div className="mb-4 h-12 flex items-center">
            {selectedIds.length > 0 ? (
            <div className="flex items-center gap-4 animate-fade-in">
                <span className="text-sm text-pro-dark-text-secondary">{selectedIds.length} sélectionné(s)</span>
                <button 
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
                >
                <HiOutlineTrash />
                Supprimer
                </button>
            </div>
            ) : (
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Rechercher..."
                className="w-full max-w-sm p-2 bg-pro-dark-surface border border-pro-dark-border rounded-md focus:ring-pro-dark-accent focus:border-pro-dark-accent"
            />
            )}
        </div>
        <Card>
            {isLoading ? (
                <p className="text-center text-pro-dark-text-secondary">Chargement des données...</p>
            ) : (
                <Table
                    columns={columns}
                    data={offresForTable}
                    onSelectionChange={setSelectedIds}
                />
            )}
        </Card>
        <Pagination
            currentPage={currentPage}
            totalItems={totalOffres}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={(page) => setCurrentPage(page)}
        />
        <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={handleDelete}
            title="Confirmer la suppression"
        >
            <p>Êtes-vous sûr de vouloir supprimer définitivement le(s) {selectedIds.length} appel(s) d'offre sélectionné(s) ?</p>
            <p className="mt-2 text-yellow-400">Cette action est irréversible.</p>
      </Modal>
    </div>
  );
};

export default OffresPage;